/**
 * TwoFactorSetupDialog — multi-step enrollment wizard.
 *
 * Path A 2FA Phase 2 (2026-05-26).
 *
 * Flow:
 *   Step 0: Intro — explain what 2FA does, "Begin setup" button
 *   Step 1: Scan QR code (with manual-secret fallback)
 *   Step 2: Save recovery codes (download as .txt, click confirm to continue)
 *   Step 3: Verify first 6-digit code → server transitions to Enrolled
 *   Step 4: Success — close + parent reloads status
 *
 * State machine — each step has explicit forward + backward transitions.
 * Server persists secret + codes after Step 1's enroll() call; the
 * "preparation" state lets the user re-mint by closing + restarting
 * (idempotent on the server side per /enroll behavior).
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';

import { twoFactorApi } from '../../api/twofa';
import { ApiError } from '../../api/client';
import { isStepUpCancelled } from '../../api/stepup';
import { translateApiError } from '../../i18n/translateApiError';

// Format secret like 'JBSWY3DPEHPK3PXP' as 'JBSW Y3DP EHPK 3PXP'
// for easier manual entry into authenticator apps.
function formatSecret(secret: string): string {
  return (secret.match(/.{1,4}/g) || []).join(' ');
}

// Build a downloadable text file from the recovery codes.
function downloadRecoveryCodes(codes: string[], orgLabel?: string) {
  const header = [
    'SVDY recovery codes',
    orgLabel ? `Account: ${orgLabel}` : '',
    `Generated: ${new Date().toISOString()}`,
    '',
    'Each code can be used ONCE. Keep them somewhere safe — for example,',
    'a password manager. If you lose access to your authenticator app,',
    'sign in with one of these codes.',
    '',
    '----------------------------------------',
    '',
  ]
    .filter(Boolean)
    .join('\n');
  const body = codes.join('\n') + '\n';
  const blob = new Blob([header + body], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `svdy-recovery-codes-${new Date().toISOString().slice(0, 10)}.txt`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface Props {
  open: boolean;
  onClose: () => void;
  onEnrolled: () => void;
  /** Optional account label shown in QR (defaults to email from token claim). */
  accountLabel?: string;
}

type Step = 'intro' | 'scan' | 'codes' | 'verify' | 'done';

const TwoFactorSetupDialog: React.FC<Props> = ({
  open,
  onClose,
  onEnrolled,
  accountLabel,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('intro');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState('');
  const [provisioningUri, setProvisioningUri] = useState('');
  const [codes, setCodes] = useState<string[]>([]);
  const [codesAcknowledged, setCodesAcknowledged] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);

  const handleClose = () => {
    if (loading) return;
    onClose();
    // Reset state so the next open starts fresh.
    setTimeout(() => {
      setStep('intro');
      setError(null);
      setSecret('');
      setProvisioningUri('');
      setCodes([]);
      setCodesAcknowledged(false);
      setVerifyCode('');
      setSecretCopied(false);
    }, 200);
  };

  const handleBeginSetup = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await twoFactorApi.enroll();
      setSecret(res.secret);
      setProvisioningUri(res.provisioning_uri);
      setCodes(res.recovery_codes);
      setStep('scan');
    } catch (e) {
      // The user declined the password prompt. Not a failure — rendering the
      // step-up demand here would tell them to do the thing they just refused
      // (owner report 2026-08-01, where it surfaced as the raw code).
      if (isStepUpCancelled(e)) return;
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.actionFailed'),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verifyCode.length !== 6) {
      setError(t('common:security.enter6DigitCode'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await twoFactorApi.verifyEnrollment(verifyCode);
      setStep('done');
      onEnrolled();
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('common:feedback.actionFailed'),
      );
      setVerifyCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard?.writeText(secret).then(() => {
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 1500);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      {/* ============================== Step: Intro ============================== */}
      {step === 'intro' && (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            {t('common:twoFactor.setup.title')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('common:twoFactor.setup.intro1')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('common:twoFactor.setup.intro2')}
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ pb: 2 }}>
            <Button onClick={handleClose} disabled={loading}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleBeginSetup}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? t('common:twoFactor.setup.starting') : t('common:twoFactor.setup.beginSetup')}
            </Button>
          </DialogActions>
        </>
      )}

      {/* ============================== Step: Scan QR ============================== */}
      {step === 'scan' && (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            {t('common:twoFactor.setup.scanTitle')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('common:twoFactor.setup.scanBody')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                p: 3,
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 2,
              }}
            >
              <QRCodeSVG value={provisioningUri} size={200} level="M" />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              {t('common:twoFactor.setup.cantScan')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.25,
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                letterSpacing: '0.1em',
              }}
            >
              <Box sx={{ flex: 1 }}>{formatSecret(secret)}</Box>
              <IconButton
                size="small"
                onClick={handleCopySecret}
                aria-label={t('common:twoFactor.setup.copySecret')}
              >
                {secretCopied ? (
                  <CheckIcon fontSize="small" color="success" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
            {accountLabel && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 1 }}
              >
                {t('common:twoFactor.setup.accountLabelPrefix')} <strong>{accountLabel}</strong>
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ pb: 2 }}>
            <Button onClick={handleClose} disabled={loading}>
              {t('common:actions.cancel')}
            </Button>
            <Button variant="contained" onClick={() => setStep('codes')}>
              {t('common:twoFactor.setup.next')}
            </Button>
          </DialogActions>
        </>
      )}

      {/* ============================== Step: Recovery codes ============================== */}
      {step === 'codes' && (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            {t('common:twoFactor.setup.codesTitle')}
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('common:twoFactor.setup.codesWarning')}
            </Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('common:twoFactor.setup.codesBody')}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 1,
                p: 2,
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.95rem',
                letterSpacing: '0.05em',
                mb: 2,
              }}
            >
              {codes.map((code, idx) => (
                <Box key={code} sx={{ color: 'text.primary' }}>
                  <Box
                    component="span"
                    sx={{ color: 'text.disabled', mr: 1 }}
                  >
                    {String(idx + 1).padStart(2, '0')}.
                  </Box>
                  {code}
                </Box>
              ))}
            </Box>
            <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => downloadRecoveryCodes(codes, accountLabel)}
              >
                {t('common:twoFactor.setup.downloadTxt')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() =>
                  navigator.clipboard?.writeText(codes.join('\n'))
                }
              >
                {t('common:twoFactor.setup.copyAll')}
              </Button>
            </Stack>
            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                userSelect: 'none',
                color: 'text.secondary',
                fontSize: '0.875rem',
              }}
            >
              <input
                type="checkbox"
                checked={codesAcknowledged}
                onChange={(e) => setCodesAcknowledged(e.target.checked)}
              />
              {t('common:twoFactor.setup.codesAcknowledge')}
            </Box>
          </DialogContent>
          <DialogActions sx={{ pb: 2 }}>
            <Button onClick={() => setStep('scan')} disabled={loading}>
              {t('common:twoFactor.setup.back')}
            </Button>
            <Button
              variant="contained"
              onClick={() => setStep('verify')}
              disabled={!codesAcknowledged}
            >
              {t('common:twoFactor.setup.continue')}
            </Button>
          </DialogActions>
        </>
      )}

      {/* ============================== Step: Verify ============================== */}
      {step === 'verify' && (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            {t('common:twoFactor.setup.verifyTitle')}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('common:twoFactor.setup.verifyBody')}
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label={t('common:security.sixDigitCode')}
              value={verifyCode}
              onChange={(e) =>
                setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' && verifyCode.length === 6 && !loading) {
                  handleVerify();
                }
              }}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 6,
                style: {
                  fontFamily: 'monospace',
                  fontSize: '1.25rem',
                  letterSpacing: '0.5rem',
                  textAlign: 'center',
                },
              }}
            />
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ pb: 2 }}>
            <Button onClick={() => setStep('codes')} disabled={loading}>
              {t('common:twoFactor.setup.back')}
            </Button>
            <Button
              variant="contained"
              onClick={handleVerify}
              disabled={loading || verifyCode.length !== 6}
              startIcon={loading ? <CircularProgress size={16} /> : null}
            >
              {loading ? t('common:twoFactor.setup.verifying') : t('common:twoFactor.setup.verifyButton')}
            </Button>
          </DialogActions>
        </>
      )}

      {/* ============================== Step: Done ============================== */}
      {step === 'done' && (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            {t('common:twoFactor.setup.doneTitle')}
          </DialogTitle>
          <DialogContent>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CheckIcon color="success" sx={{ fontSize: 32 }} />
              <Typography variant="body1">
                {t('common:twoFactor.setup.doneBody1')}
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, display: 'block' }}
            >
              {t('common:twoFactor.setup.doneBody2')}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ pb: 2 }}>
            <Button variant="contained" onClick={handleClose}>
              {t('common:twoFactor.setup.done')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default TwoFactorSetupDialog;
